import cn from 'classnames';
import { FC, useLayoutEffect } from 'react';
import { useIntl } from 'react-intl';

import { scrollToElementByID } from '@/shared/lib';
import { Accordion } from '@/shared/ui';

import {
  CompactCondition,
  ConditionDecisionChange,
  ConditionGroups,
  globalConditionGroupText,
} from '../../config';
import { useConditionGroupsList } from '../../hooks/useConditionGroupsList';
import { ConditionChildGroup } from '../ConditionChildGroup/ConditionChildGroup';
import { ConditionToggle } from '../ConditionToggle/ConditionToggle';
import { ConditionToggleContainer } from '../ConditionToggleContainer/ConditionToggleContianer';
import { ConditionToggleList } from '../ConditionToggleList/ConditionToggleList';

import styles from './ConditionListByCategory.module.scss';

type ConditionListByCategoryProps = {
  className?: string;
  testID?: string;
  conditionItemsByGroups: Record<ConditionGroups, CompactCondition[]>;
  updateConditionDecision: ConditionDecisionChange;
  disabled?: boolean;
  toothID: string;
  conditionID?: string;
  openAll?: boolean;
};

export const ConditionListByCategory: FC<ConditionListByCategoryProps> = (
  props,
) => {
  const {
    className,
    testID,
    conditionItemsByGroups,
    updateConditionDecision,
    disabled,
    conditionID,
    openAll,
  } = props;

  const { formatMessage } = useIntl();

  const { conditionGroupsList, openConditionGroups } = useConditionGroupsList(
    conditionItemsByGroups,
    openAll,
  );

  useLayoutEffect(() => {
    const scrollWithDelay = () => {
      if (conditionID) {
        scrollToElementByID(conditionID, 'smooth', 'start');
      }
    };

    const timeoutID = setTimeout(scrollWithDelay, 300);

    return () => clearTimeout(timeoutID);
  }, [conditionID]);

  return (
    <div
      className={cn(styles.container, className)}
      data-testid={testID}
      data-section="condition-list"
    >
      <Accordion.Root
        type="multiple"
        defaultValue={openConditionGroups}
        className={cn(styles.list)}
      >
        {conditionGroupsList.map((group: ConditionGroups) => (
          <Accordion.Item key={group} value={group}>
            <Accordion.Header>
              <Accordion.Trigger>
                {formatMessage(
                  globalConditionGroupText[group as ConditionGroups],
                )}
              </Accordion.Trigger>
            </Accordion.Header>

            <Accordion.Content>
              <ConditionToggleList className={styles.content}>
                {conditionItemsByGroups[group].map((data) =>
                  data.childConditionInterfaces.length > 0 ? (
                    <ConditionToggleContainer key={data.code}>
                      <ConditionToggle
                        className={styles.conditionLabel}
                        data={data}
                        onChange={updateConditionDecision}
                        disabled={disabled}
                      />

                      <ConditionChildGroup
                        className={styles.childGroup}
                        data={data.childConditionInterfaces}
                        onChange={updateConditionDecision}
                        disabled={disabled}
                      />
                    </ConditionToggleContainer>
                  ) : (
                    <ConditionToggle
                      key={data.code}
                      data={data}
                      onChange={updateConditionDecision}
                      disabled={disabled}
                    />
                  ),
                )}
              </ConditionToggleList>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </div>
  );
};
